import { RepeatClockIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Divider,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { getDateString, getDateTimeString, StageHistoryItem } from '@hb/shared'
import { PropsWithChildren, useMemo } from 'react'

import { HistoryNote } from '../../../../Assessments/HistoryNote'
import { UserBadge } from '../../../UserBadge'

const formatStageHistoryOperation = (
  operation: StageHistoryItem<string>['operation'],
): {
  action: string
  suffix: string
} => {
  switch (operation) {
    case 'add':
      return { action: 'Added', suffix: 'to' }
    case 'remove':
      return { action: 'Removed', suffix: 'from' }
    case 'move':
      return { action: 'Moved', suffix: 'to' }
    default:
      return { action: operation, suffix: '' }
  }
}

// const getOperationIcon = (operation: StageHistoryItem<string>['operation']) => {
//   switch (operation) {
//     case 'add':
//       return <AddIcon w={3} h={3} opacity={0.7} />
//     case 'remove':
//       return <MinusIcon w={3} h={3} opacity={0.7} />
//     case 'move':
//       return <ArrowForwardIcon w={4} h={4} opacity={0.7} />
//     default:
//       return null
//   }
// }

const StatusHistoryItem = ({ item }: { item: StageHistoryItem<string> }) => {
  const { stages, by, group, operation, on, notes } = item
  const { action, suffix } = formatStageHistoryOperation(operation)
  return (
    <Flex flexFlow="column" w="100%" p={2} gap={1} align="flex-start">
      <Flex gap={1.5} w="100%" align="flex-start">
        <Flex flex={1} minW="0" align="center" gap={1}>
          <Text opacity={0.8} fontSize="sm" fontWeight={600}>
            {/* <span style={{ position: 'relative', bottom: '2px', marginRight: '2px' }}>
              {getOperationIcon(operation)}
            </span> */}
            {action} {suffix} {stages.join(', ')}
          </Text>
        </Flex>
        <Flex align="center" gap={1}>
          <Tooltip placement="top" hasArrow bg="gray.600" label={getDateTimeString(on, 'short')}>
            <Text ml="auto" fontSize="xs" fontWeight={600} color="gray.600">
              {getDateString(on, 'short')}
            </Text>
          </Tooltip>
          <UserBadge userId={by} userGroup={group} />
        </Flex>
      </Flex>
      {notes ? <HistoryNote>{notes}</HistoryNote> : null}
    </Flex>
  )
}

const PregnancyStatusHistory = <Stage extends string>({
  stageHistory,
}: {
  stageHistory: StageHistoryItem<Stage>[] | undefined
}) => {
  const reversedStageHistory = useMemo(() => [...(stageHistory ?? [])].toReversed(), [stageHistory])
  return (
    <VStack spacing={0} divider={<Divider />}>
      {reversedStageHistory.length ? (
        reversedStageHistory.map(h => (
          <StatusHistoryItem key={`${h.operation}_${h.stages.join('-')}_${h.on}`} item={h} />
        ))
      ) : (
        <Text p={2} color="gray.700" fontStyle="italic">
          No history to show
        </Text>
      )}
    </VStack>
  )
}

export const PregnancyStatusHistoryPopover = <Stage extends string>({
  stageHistory,
  children,
}: PropsWithChildren<{
  stageHistory: StageHistoryItem<Stage>[] | undefined
}>) => (
  <Popover trigger="hover">
    <PopoverTrigger>
      <Box>{children}</Box>
    </PopoverTrigger>
    <PopoverContent>
      <PopoverBody maxH="350px" overflowY="auto" p={0}>
        <PregnancyStatusHistory stageHistory={stageHistory} />
      </PopoverBody>
      <PopoverArrow />
    </PopoverContent>
  </Popover>
)

export const PregnancyStatusHistoryButton = <Stage extends string>({
  stageHistory,
}: {
  stageHistory: StageHistoryItem<Stage>[] | undefined
}) => (
  <PregnancyStatusHistoryPopover stageHistory={stageHistory}>
    <Button
      aria-label="History"
      variant="outline"
      gap={1}
      bg="gray.50"
      borderColor="gray.400"
      color="gray.600"
      rowGap={1.5}
      size="xs"
      fontSize="sm"
      borderRadius="full">
      <RepeatClockIcon />
      <Text>HISTORY</Text>
    </Button>
  </PregnancyStatusHistoryPopover>
)
